import type { ValueOf } from 'type-fest';

import { theme } from '@frontend/components';

import { isDigitalQuoteDomain } from 'utils/siteUtils';
import { getToken } from 'utils/token';

const getApiUrl = () => {
  const apiUrl = isDigitalQuoteDomain()
    ? process.env.DIGIQUOTE_API_URL
    : process.env.API_URL;
  const protocolPrefix =
    typeof location !== 'undefined' ? location.protocol + '//' : 'http://';
  const isProtocolSpecified = /http.?:\/\//.test(apiUrl);
  const isInsideContainer = location.hostname === 'nginx';
  return isInsideContainer
    ? protocolPrefix + 'nginx/api'
    : isProtocolSpecified
    ? apiUrl
    : protocolPrefix + apiUrl;
};

export const getApiHeaders = () => {
  const headers = {
    Accept: 'application/json',
  };

  const token = getToken();
  if (token) {
    headers.Authorization = 'Token ' + token;
  }

  return headers;
};

export const SERVER = process.env.SERVER;
export const ENV = process.env.NODE_ENV;

export const API_URL = getApiUrl();

export const IDP_URL = API_URL.replace(/\/api^/, '');

export const SOCKETS_URL = process.env.SOCKETS_URL;

export const CUBE_URL = process.env.CUBE_URL;

export const MARKETING_SITE_URL = 'https://www.paperlessparts.com';

export const STATIC_URL = 'https://s3.amazonaws.com/static.paperlessparts.com';

export const SUPPORT_EMAIL = 'support@paperlessparts.com';
export const SUPPORT_PHONE_NUMBER = '+1 617-858-5731';

export const DATADOG_SERVICE = process.env.DATADOG_SERVICE;
export const DATADOG_CLIENT_TOKEN = process.env.DATADOG_CLIENT_TOKEN;
export const DATADOG_RUM_APP_ID = process.env.DATADOG_RUM_APP_ID;
export const DATADOG_RUM_CLIENT_TOKEN = process.env.DATADOG_RUM_CLIENT_TOKEN;

export const LOGGING = process.env.LOGGING === 'true';
export const STRIPE_API_KEY = process.env.STRIPE_API_KEY;
export const PDF_TRON_KEY = process.env.PDF_TRON_KEY;
export const PDF_TRON_PATH = `/${process.env.PDF_TRON_PATH}`.replace(
  /\/+/g,
  '/',
);
export const RELEASE = process.env.RELEASE;
export const CURRENT_YEAR = new Date().getFullYear();

export const PAGE_SIZE = 20;

export const ROLES = {
  SUPPLIER: 'supplier',
  BUYER: 'buyer',
};

export const KG_PER_LB = 0.4535924;

export const UNITS = {
  IN: 'in',
  MM: 'mm',
} as const;

export const WEIGHT_UNITS = {
  KG: 'kg', //mass interpretted as weight
  LB: 'lb',
};

export const CAD_FILE_TYPES = {
  SLDPRT: 'sldprt',
  STEP: 'step',
  STL: 'stl',
  STP: 'stp',
  STPZ: 'stpz',
  DAE: 'dae',
  DWG: 'dwg',
  DWF: 'dwf',
  DWFX: 'dwfx',
  DXF: 'dxf',
  EXP: 'exp',
  GLB: 'glb',
  THREE_MF: '3mf',
  THREE_DM: '3dm',
  THREE_DS: '3ds',
  THREE_DXML: '3dxml',
  CATPART: 'catpart',
  CATSHAPE: 'catshape',
  CGR: 'cgr',
  IFC: 'ifc',
  IGES: 'iges',
  IGS: 'igs',
  IPT: 'ipt',
  JT: 'jt',
  OBJ: 'obj',
  MODEL: 'model',
  NEU: 'neu',
  PRC: 'prc',
  PRT: 'prt',
  PAR: 'par',
  PSM: 'psm',
  SAB: 'sab',
  SAT: 'sat',
  VRML: 'vrml',
  WRL: 'wrl',
  X_B: 'x_b',
  X_T: 'x_t',
  ZIP: 'zip',
  SEVEN_ZIP: '7z',
} as const;

//cad file types ranked by how well we support them
export const RANKED_CAD_FILE_TYPES = {
  [CAD_FILE_TYPES.STEP]: 1,
  [CAD_FILE_TYPES.STP]: 2,
  [CAD_FILE_TYPES.STPZ]: 3,
  [CAD_FILE_TYPES.SLDPRT]: 4,
  [CAD_FILE_TYPES.X_B]: 5,
  [CAD_FILE_TYPES.X_T]: 6,
  [CAD_FILE_TYPES.IPT]: 7,
  [CAD_FILE_TYPES.PRT]: 8,
  [CAD_FILE_TYPES.SAT]: 9,
  [CAD_FILE_TYPES.SAB]: 10,
  [CAD_FILE_TYPES.CATPART]: 11,
  [CAD_FILE_TYPES.CATSHAPE]: 12,
  [CAD_FILE_TYPES.MODEL]: 13,
  [CAD_FILE_TYPES.EXP]: 14,
  [CAD_FILE_TYPES.PAR]: 15,
  [CAD_FILE_TYPES.IGES]: 16,
  [CAD_FILE_TYPES.IGS]: 17,
  [CAD_FILE_TYPES.PRC]: 18,
  [CAD_FILE_TYPES.PSM]: 19,
  [CAD_FILE_TYPES.NEU]: 20,
  [CAD_FILE_TYPES.CGR]: 21,
  [CAD_FILE_TYPES.IFC]: 22,
  [CAD_FILE_TYPES.THREE_DM]: 23,
  [CAD_FILE_TYPES.DAE]: 24,
  [CAD_FILE_TYPES.DWG]: 25,
  [CAD_FILE_TYPES.DXF]: 26,
  [CAD_FILE_TYPES.STL]: 27,
  [CAD_FILE_TYPES.JT]: 28,
  [CAD_FILE_TYPES.OBJ]: 29,
  [CAD_FILE_TYPES.THREE_DXML]: 30,
  [CAD_FILE_TYPES.THREE_MF]: 31,
  [CAD_FILE_TYPES.THREE_DS]: 32,
  [CAD_FILE_TYPES.DWF]: 33,
  [CAD_FILE_TYPES.DWFX]: 34,
  [CAD_FILE_TYPES.VRML]: 35,
  [CAD_FILE_TYPES.WRL]: 36,
  [CAD_FILE_TYPES.GLB]: 37,
  [CAD_FILE_TYPES.ZIP]: 38,
  [CAD_FILE_TYPES.SEVEN_ZIP]: 39,
} as const satisfies Record<ValueOf<typeof CAD_FILE_TYPES>, number>;

export const UNITLESS_FILE_TYPES = [
  CAD_FILE_TYPES.OBJ,
  CAD_FILE_TYPES.STL,
  CAD_FILE_TYPES.THREE_DS,
];

export const IMG_FILE_TYPES = {
  APNG: 'apng',
  BMP: 'bmp',
  CUR: 'cur',
  GIF: 'gif',
  ICO: 'ico',
  JPG: 'jpg',
  JPEG: 'jpeg',
  JFIF: 'jfif',
  PJPEG: 'pjpeg',
  PJP: 'pjp',
  PNG: 'png',
  SVG: 'svg',
  WEBP: 'webp',
};

const MIME_TYPES = {
  MP4: 'video/mp4',
  OGG: 'video/ogg',
  WEBM: 'video/webm',
};

export const PDF_FILE_TYPES = {
  PDF: 'pdf',
};

export const VECTOR_EDITOR_FILE_TYPES = {
  PDF: 'pdf',
  DXF: 'dxf',
};

export const CONVERTIBLE_FILES_TYPES = {
  DOC: 'doc',
  DOCX: 'docx',
  PPTX: 'pptx',
  PPT: 'ppt',
  PPS: 'pps',
  XLS: 'xls',
  XLT: 'xlt',
  XLST: 'xlst',
  XLSX: 'xlsx',
  XML: 'xml',
  CSV: 'csv',
  TIF: 'tif',
  TIFF: 'tiff',
  BIB: 'bib',
  FODT: 'fodt',
  HTML: 'html',
  LTX: 'ltx',
  TXT: 'txt',
  ODT: 'odt',
  OTT: 'ott',
  PDP: 'pdb',
  PSW: 'psw',
  RTF: 'rtf',
  SDW: 'sdw',
  STW: 'stw',
  SXW: 'sxw',
  UOT: 'uot',
  WOR: 'vor',
  WPS: 'wps',
  EPUB: 'epub',
  EMF: 'emf',
  EPS: 'eps',
  FODG: 'fodg',
  MET: 'met',
  ODD: 'odd',
  OTG: 'otg',
  PBM: 'pbm',
  PCT: 'pct',
  PGM: 'pgm',
  PPM: 'ppm',
  RAS: 'ras',
  STD: 'std',
  SVM: 'svm',
  SWF: 'swf',
  SXD: 'sxd',
  XHTML: 'xhtml',
  XPM: 'xpm',
  FODP: 'fodp',
  POTM: 'potm',
  POT: 'pot',
  PWP: 'pwp',
  SDA: 'sda',
  SSD: 'ssd',
  STI: 'sti',
  SXI: 'sxi',
  UOP: 'uop',
  WMF: 'wmf',
  DBF: 'dbf',
  DIF: 'dif',
  FODS: 'fods',
  ODS: 'ods',
  OTS: 'ots',
  PXL: 'pxl',
  SDC: 'sdc',
  SLK: 'slk',
  STC: 'stc',
  SXC: 'sxc',
  UOS: 'uos',
  ODP: 'odp',
  MSG: 'msg',
  EML: 'eml',
};

export const VIDEO_FILE_TYPES = {
  F4A: 'f4a',
  F4B: 'f4b',
  F4V: 'f4v',
  M4A: 'm4a',
  M4B: 'm4b',
  M4R: 'm4r',
  M4V: 'm4v',
  MOV: 'mov',
  MP4: 'mp4',
  OGA: 'oga',
  OGG: 'ogg',
  OGV: 'ogv',
  OGX: 'ogx',
  WEBM: 'webm',
};

export const VIDEO_FILE_MIME_TYPES = {
  [VIDEO_FILE_TYPES.F4A]: [MIME_TYPES.MP4],
  [VIDEO_FILE_TYPES.F4B]: [MIME_TYPES.MP4],
  [VIDEO_FILE_TYPES.F4V]: [MIME_TYPES.MP4],
  [VIDEO_FILE_TYPES.M4A]: [MIME_TYPES.MP4],
  [VIDEO_FILE_TYPES.M4B]: [MIME_TYPES.MP4],
  [VIDEO_FILE_TYPES.M4R]: [MIME_TYPES.MP4],
  [VIDEO_FILE_TYPES.M4V]: [MIME_TYPES.MP4],
  [VIDEO_FILE_TYPES.MOV]: [MIME_TYPES.MP4],
  [VIDEO_FILE_TYPES.MP4]: [MIME_TYPES.MP4],
  [VIDEO_FILE_TYPES.OGA]: [MIME_TYPES.OGG],
  [VIDEO_FILE_TYPES.OGG]: [MIME_TYPES.OGG],
  [VIDEO_FILE_TYPES.OGV]: [MIME_TYPES.OGG],
  [VIDEO_FILE_TYPES.OGX]: [MIME_TYPES.OGG],
  [VIDEO_FILE_TYPES.WEBM]: [MIME_TYPES.WEBM],
};

export const WEBSERVICES_TO_VIEWER_ORIENTATION = {
  z: 'z',
  z_neg: '-z',
  y: 'y',
  y_neg: '-y',
  x: 'x',
  x_neg: '-x',
};

export const ONLINE_METALS = 'onlineMetals';

export const ONLINE_METALS_LOGO =
  'https://s3.amazonaws.com/static.paperlessparts.com/OnlineMetals-Logo.svg';
export const ONLINE_METALS_WIDGET_LOGO_BLACK =
  'https://s3.amazonaws.com/static.paperlessparts.com/online-metals-widget-logo-black.png';
export const ONLINE_METALS_WIDGET_LOGO_WHITE =
  'https://s3.amazonaws.com/static.paperlessparts.com/online-metals-widget-logo-white.png';

export const PEM_LOGO =
  'https://s3.amazonaws.com/static.paperlessparts.com/images/pem.png';
export const PEM_LOGO_WHITE =
  'https://s3.amazonaws.com/static.paperlessparts.com/images/pem_white.png';

export const PENN_ENGINEERING_LOGO =
  'https://s3.amazonaws.com/static.paperlessparts.com/images/penn_engineering.png';

export const PEM_CONNECT_EULA = 'pem_connect_eula';

export const MATERIAL_PROVIDERS = {
  [ONLINE_METALS]: {
    displayName: 'Online Metals',
    name: ONLINE_METALS,
    logo: ONLINE_METALS_LOGO,
    livePricingLogo: ONLINE_METALS_LOGO,
    urlParam: 'online-metals',
  },
};

export const PDF_LOGO =
  'https://s3.amazonaws.com/static.digital-quote.com/icons/pdf4.svg';

export const FILE_ICON =
  'https://s3.amazonaws.com/static.digital-quote.com/icons/file.svg';

export const MALWARE_ICON =
  'https://s3.amazonaws.com/static.paperlessparts.com/images/malware.svg';

export const QUOTE_STATUS = {
  ALL: 'all',
  DRAFT: 'draft',
  UNVIEWED_DRAFT_RFQ: 'unviewed_draft_rfq',
  OUTSTANDING: 'outstanding',
  EXPIRED: 'expired',
  ACCEPTED: 'accepted',
  CANCELLED: 'cancelled',
  LOST: 'lost',
  TRASH: 'trash',
} as const;

export const QUOTE_STATUS_LABEL = {
  [QUOTE_STATUS.ALL]: 'all',
  [QUOTE_STATUS.DRAFT]: 'draft',
  [QUOTE_STATUS.OUTSTANDING]: 'outstanding',
  [QUOTE_STATUS.EXPIRED]: 'expired',
  [QUOTE_STATUS.ACCEPTED]: 'accepted',
  [QUOTE_STATUS.CANCELLED]: 'cancelled',
  [QUOTE_STATUS.LOST]: 'lost',
  [QUOTE_STATUS.TRASH]: 'in trash',
  [QUOTE_STATUS.UNVIEWED_DRAFT_RFQ]: 'unviewed rfq',
};

export const QUOTE_ALIVE_STATUSES = {
  [QUOTE_STATUS.DRAFT]: true,
  [QUOTE_STATUS.OUTSTANDING]: true,
  [QUOTE_STATUS.EXPIRED]: false,
  [QUOTE_STATUS.ACCEPTED]: true,
  [QUOTE_STATUS.CANCELLED]: false,
  [QUOTE_STATUS.TRASH]: false,
};

export const ORDER_STATUS = {
  ALL: 'all',
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  ON_HOLD: 'on_hold',
  IN_PROCESS: 'in_process',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
} as const;

export const ORDER_STATUS_LABEL = {
  [ORDER_STATUS.ALL]: 'All',
  [ORDER_STATUS.PENDING]: 'New',
  [ORDER_STATUS.CONFIRMED]: 'Confirmed',
  [ORDER_STATUS.ON_HOLD]: 'On hold',
  [ORDER_STATUS.IN_PROCESS]: 'In process',
  [ORDER_STATUS.COMPLETED]: 'Completed',
  [ORDER_STATUS.CANCELLED]: 'Cancelled',
};

export const QUOTE_SEND_WARNING_TYPES = {
  INCOMPLETE_WARNING: 'INCOMPLETE_WARNING',
  LEAD_TIME_WARNING: 'LEAD_TIME_WARNING',
  MANUFACTURABILITY_WARNING: 'MANUFACTURABILITY_WARNING',
  PRICE_WARNING: 'PRICE_WARNING',
  QUANTITY_WARNING: 'QUANTITY_WARNING',
  SHIPPING_ESTIMATION_WARNING: 'SHIPPING_ESTIMATION_WARNING',
  ZERO_SHIPPING_WARNING: 'ZERO_SHIPPING_WARNING',
  CREDIT_CARD_CHECKOUT_DISABLED_WARNING:
    'CREDIT_CARD_CHECKOUT_DISABLED_WARNING',
  OPEN_ACTIONS_WARNING: 'OPEN_ACTIONS_WARNING',
};

export const QUOTE_SEND_WARNING_MESSAGES = {
  [QUOTE_SEND_WARNING_TYPES.PRICE_WARNING]: 'is missing prices.',
  [QUOTE_SEND_WARNING_TYPES.QUANTITY_WARNING]: 'has no quantities.',
  [QUOTE_SEND_WARNING_TYPES.LEAD_TIME_WARNING]: 'is missing lead times.',
  [QUOTE_SEND_WARNING_TYPES.MANUFACTURABILITY_WARNING]:
    'has a manufacturing challenge.',
  [QUOTE_SEND_WARNING_TYPES.ZERO_SHIPPING_WARNING]:
    'This quote has $0 shipping.',
  [QUOTE_SEND_WARNING_TYPES.SHIPPING_ESTIMATION_WARNING]:
    'Cannot calculate shipping costs for this item. If buyer selects this item, it will NOT be factored into shipping price.',
};

export const CREDIT_CARD_PAYMENT_METHOD = {
  NEW: 'NEW',
  EXISTING: 'EXISTING',
};

export const PAYMENT_METHOD = {
  NONE: null,
  CREDIT_CARD: 'credit_card',
  PURCHASE_ORDER: 'purchase_order',
};

export const TWO_FACTOR_AUTH_STATUS = {
  ENABLED: 'Enabled',
  DISABLED: 'Disabled',
};

export const SHIPPING_TYPE = {
  PICKUP: 'pickup',
  CUSTOMER_ACCOUNT: 'customers_shipping_account',
  SUPPLIER_ACCOUNT: 'suppliers_shipping_account',
} as const;

export type ShippingType = ValueOf<typeof SHIPPING_TYPE>;

export const SHIPPING_CARRIER = {
  UPS: 'ups',
  FEDEX: 'fedex',
} as const;

export const SHIPPING_GROUPING = {
  BULK: 'all_at_once',
  BY_LEAD_TIME: 'when_ready',
};

export const SHIPPING_CARRIER_LABEL = {
  [SHIPPING_CARRIER.UPS]: 'UPS',
  [SHIPPING_CARRIER.FEDEX]: 'FedEx',
} as const;

export const OBTAIN_METHOD = {
  MANUFACTURED: 'manufactured',
  PURCHASED: 'purchased',
} as const;

export const BINARY_PERMISSIONS = {
  NO_ACCESS: 'NO_ACCESS',
  ACCESS: 'ACCESS',
} as const;

export const BASE_PERMISSIONS = {
  NO_ACCESS: 'NO_ACCESS',
  READ_ONLY: 'READ_ONLY',
  EDIT: 'EDIT',
} as const;

export const QUOTES_PERMISSIONS = {
  NO_ACCESS: 'NO_ACCESS',
  READ_ONLY: 'READ_ONLY',
  DRAFT: 'DRAFT',
  FINALIZE: 'FINALIZE',
} as const;

export const INTEGRATION_PERMISSIONS = {
  NO_ACCESS: 'NO_ACCESS',
  READ_ONLY: 'READ_ONLY',
  REQUEST_ACTIONS: 'REQUEST_ACTIONS',
  MANAGE_BEHAVIOR: 'MANAGE_BEHAVIOR',
  CONFIGURE: 'CONFIGURE',
};

export const DOWNLOAD_PART_FILES_PERMISSIONS = BINARY_PERMISSIONS;
export const EXTERNAL_SHARING_PERMISSIONS = BASE_PERMISSIONS;

// DEPRECATED: Use specific permissions instead based on permissions defined for that resource type in supplier roles
export const PERMISSIONS = {
  NO_ACCESS: 'NO_ACCESS',
  READ_ONLY: 'READ_ONLY',
  DRAFT: 'DRAFT',
  FINALIZE: 'FINALIZE',
  EDIT: 'EDIT',
  ACCESS: 'ACCESS',
  REQUEST_ACTIONS: 'REQUEST_ACTIONS',
  MANAGE_BEHAVIOR: 'MANAGE_BEHAVIOR',
  CONFIGURE: 'CONFIGURE',
} as const;

export const SUPPLIER_ROLES = {
  ACCOUNTING: 'accounting',
  ANALYTICS: 'analytics',
  CUSTOMERS: 'customers',
  DIGI_QUOTE_SETTINGS: 'digiquoteSettings',
  INTEGRATIONS: 'integrations',
  ORDERS: 'orders',
  PROCESSES: 'processes',
  QUOTES: 'quotes',
  SUPPLIER_SETTINGS: 'supplierSettings',
  TEAM: 'team',
  CONTROLLED_INFO: 'controlledInfo',
} as const;

export const PAPERLESS_TERMS_TYPES = {
  FABRICATOR_TERMS: 'fabricator_terms',
  PRIVACY_POLICY: 'privacy_policy',
  WEB_SERVICE_AGREEMENT: 'web_service_agreement',
};

/**
 * @deprecated use PART_STATUSES from @frontend/api package instead
 */
export const PART_STATUSES = {
  FINISHED: 'FINISHED',
  IN_PROGRESS: 'IN_PROGRESS',
  QUEUED: 'QUEUED',
};

export const GEOMETRIC_INTERROGATION_STATUSES = {
  PROCESSING: 'processing',
  FAILED: 'failed',
  SUCCESSFUL: 'successful',
};

/**
 * @deprecated use PROCESSING_CATEGORIES from @frontend/api package instead
 */
export const PROCESSING_CATEGORIES = {
  PROCESSING: 'processing',
  VIEWER_CREATION: 'viewerCreation',
  THUMBNAIL_CREATION: 'thumbnailCreation',
  GEOMETRIC_INDEXING: 'geometricIndexing',
  UPLOAD: 'upload',
  VECTORIZATION: 'vectorization',
};

export const ADDITIVE_TASK = 'additive';
export const CASTING_TASK = 'casting';
export const LATHE_TASK = 'lathe';
export const MILL3_TASK = 'mill3';
export const SHEET_METAL_TASK = 'sheet_metal';
export const TUBE_LASER_TASK = 'tube_laser';
export const WIRE_EDM_TASK = 'wire_edm';

export const PROCESS_TAGS = [
  {
    label: 'Additive',
    processTag: ADDITIVE_TASK,
    value: ADDITIVE_TASK,
  },
  {
    label: 'Cast Urethane',
    processTag: CASTING_TASK,
    value: CASTING_TASK,
  },
  {
    label: 'CNC Milling',
    processTag: MILL3_TASK,
    value: MILL3_TASK,
  },
  {
    label: 'CNC Lathe / Turning',
    processTag: LATHE_TASK,
    value: LATHE_TASK,
  },
  {
    label: 'Sheet Metal',
    processTag: SHEET_METAL_TASK,
    value: SHEET_METAL_TASK,
  },
  {
    label: 'Tube Laser',
    processTag: TUBE_LASER_TASK,
    value: TUBE_LASER_TASK,
  },
  {
    label: 'Wire EDM',
    processTag: WIRE_EDM_TASK,
    value: WIRE_EDM_TASK,
  },
];

export const VIEWER_MODES = {
  SHARE: 'share', // mode for shares
  OWNER: 'viewer', // mode for resource owners
};

export const MSC_LOGO_LINK =
  'https://s3.amazonaws.com/static.paperlessparts.com/images/msc-logo-red-no-tagline.png';

export const MSC_LOGO_MINI_RED_LINK =
  'https://s3.amazonaws.com/static.paperlessparts.com/images/msc_logo_mini_red.png';
export const MSC_LOGO_MINI_WHITE_LINK =
  'https://s3.amazonaws.com/static.paperlessparts.com/images/msc_logo_mini_white.png';

export const MSC_TECHMATE_EULA = 'msc_techmate_eula';

export const TECHMATE_LOGO_LINK =
  'https://s3.amazonaws.com/static.paperlessparts.com/images/msc-logo-TechMate-black.png';

export const STOCK_TYPES = {
  RECTANGULAR: 'rectangular',
  CYLINDRICAL: 'cylindrical',
  SHEET: 'sheet',
  NESTING: 'nesting',
};

export const STOCK_BAR_LEGEND_IMG_URL =
  'https://s3.amazonaws.com/static.paperlessparts.com/images/bar-rod-modal.jpg';

export const STOCK_SHEET_LEGEND_IMG_URL =
  'https://s3.amazonaws.com/static.paperlessparts.com/images/om-modal2.jpg';

export const STOCK_STATUSES = {
  INTERROGATING: 'INTERROGATING',
  SUCCESSFUL: 'SUCCESSFUL',
  FAILED: 'FAILED',
};

export const LEAD_TIME_UNITS = {
  business_days: {
    value: 'business_days',
    label: 'Business Days',
    unitName: 'Day',
  },
  calendar_days: {
    value: 'calendar_days',
    label: 'Calendar Days',
    unitName: 'Day',
  },
  weeks: {
    value: 'weeks',
    label: 'Weeks',
    unitName: 'Week',
  },
} as const;

export type LeadTimeUnit = keyof typeof LEAD_TIME_UNITS;

export type LeadTimeUnits = ValueOf<typeof LEAD_TIME_UNITS>;

export const DETAILS_SETTINGS_MODAL_TYPES = {
  QUOTE_ITEM_UPLOAD: 'QUOTE_ITEM_UPLOAD',
  COMPONENT_UPLOAD: 'COMPONENT_UPLOAD',
  QUOTE_ITEM_CREATE: 'QUOTE_ITEM_CREATE',
  COMPONENT_CREATE: 'COMPONENT_CREATE',
  BULK_COMPONENT_UPDATE: 'BULK_COMPONENT_UPDATE',
  SUB_ASSEMBLY_CREATE: 'SUB_ASSEMBLY_CREATE',
} as const;

export const FORMULA_TYPES = {
  ADD_ON: 'add_on',
  OPERATION: 'operation',
  PROCESS: 'process',
  PROFIT_ITEM: 'profit_item',
  DISCOUNT: 'discount',
};

export const FEATURE_INDICATOR = 'fea';
export const FEEDBACK_INDICATOR = 'fee';

export const ASSEMBLY_TABLE_TYPES = {
  CHILD_BOM: 'CHILD_BOM',
  FLAT_BOM: 'FLAT_BOM',
};

export const VECTOR_FILE_EDITOR_MODES = {
  VECTORIZE: 'vectorize',
  CONTOUR: 'contour',
};

export const SUPPLIER_TABLE_COLUMN_TYPES = {
  NUMERIC: 'numeric',
  STRING: 'string',
  BOOLEAN: 'boolean',
};

export const ASSEMBLY_TOOLTIP_DESCRIPTIONS = {
  ASSEMBLY_TOTAL_COST:
    'Cost of all subassembly-level operations plus the sum of all direct child component costs (child unit cost multiplied by node quantity)',
  ASSEMBLY_UNIT_COST:
    'Unit cost of all subassembly-level operations plus the sum of all direct child component costs (child unit cost multiplied by node quantity)',
  BOM_QUANTITY: 'Number of components delivered to customer',
  CHILD_BOM_ROOT:
    'List of components that are direct children to this component',
  CHILD_BOM_SUB:
    'List of components that are direct children to this component',
  CHILD_QUANTITY_ROOT:
    'Number of times the component appears at this location in the BOM',
  CHILD_QUANTITY_SUB:
    'Number of times the component appears at this location in the BOM',
  COST_TO_ASSEMBLE: 'Cost of all subassembly-level operations',
  FLAT_BOM: 'Complete list of components that appear anywhere in the assembly',
  FLAT_QUANTITY:
    'Number of times the component appears in the entire assembly tree',
  MAKE_QUANTITY_COMPONENT:
    'Number of units to produce, factoring in yield. Yield compounds at each assembly level.',
  MAKE_QUANTITY_ROOT: 'Number of units to produce, factoring in yield',
  MAIN_QUANTITY: 'Customer requested quantity',
  TOTAL_COST_CONTRIBUTION_ASSEMBLY_ROOT:
    'Child unit cost multiplied by node quantity multiplied by parent BOM quantity (for root component, parent BOM quantity is equal to customer requested quantity)',
  TOTAL_COST_CONTRIBUTION_ASSEMBLY_SUB:
    'Child unit cost multiplied by node quantity multiplied by parent BOM quantity (for root component, parent BOM quantity is equal to customer requested quantity)',
  TOTAL_COST_CONTRIBUTION_TOTAL:
    'Sum of materials and operations or unit cost override',
  YIELD_ASSEMBLY:
    'Percentage of non-defective units of all produced units. Assumes all child component units also defective for each defective assembled unit.',
  YIELD_COMPONENT: 'Percentage of non-defective units of all produced units',
  YIELD_SUBASSEMBLY:
    'Percentage of non-defective units of all produced units. NOTE: Must be 100% for subassemblies.',
};

export const ACCOUNT_TYPES = {
  CUSTOMER: 'customer',
  VENDOR: 'vendor',
};

export const QUOTE_ITEM_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  NO_QUOTE: 'no_quote',
  COMPLETED: 'completed',
  ON_HOLD: 'on_hold',
};

export const QUOTE_ITEM_STATUS_COLOR = {
  [QUOTE_ITEM_STATUS.NOT_STARTED]: theme.colors.status.notStarted,
  [QUOTE_ITEM_STATUS.IN_PROGRESS]: theme.colors.status.inProgress,
  [QUOTE_ITEM_STATUS.NO_QUOTE]: theme.colors.status.notApplicable,
  [QUOTE_ITEM_STATUS.COMPLETED]: theme.colors.status.complete,
  [QUOTE_ITEM_STATUS.ON_HOLD]: theme.colors.status.onHold,
};

export const QUOTE_ITEM_STATUS_LABEL = {
  [QUOTE_ITEM_STATUS.NOT_STARTED]: 'Not Started',
  [QUOTE_ITEM_STATUS.IN_PROGRESS]: 'In Progress',
  [QUOTE_ITEM_STATUS.NO_QUOTE]: 'No Quote',
  [QUOTE_ITEM_STATUS.COMPLETED]: 'Completed',
  [QUOTE_ITEM_STATUS.ON_HOLD]: 'On Hold',
};

export const WORKFLOW_STEP_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  ON_HOLD: 'on_hold',
  NOT_APPLICABLE: 'not_applicable',
};

export const WORKFLOW_STEP_STATUS_LABEL = {
  [WORKFLOW_STEP_STATUS.NOT_STARTED]: 'Not Started',
  [WORKFLOW_STEP_STATUS.IN_PROGRESS]: 'In Progress',
  [WORKFLOW_STEP_STATUS.COMPLETED]: 'Completed',
  [WORKFLOW_STEP_STATUS.ON_HOLD]: 'On Hold',
  [WORKFLOW_STEP_STATUS.NOT_APPLICABLE]: 'N/A',
};

export const WORKFLOW_OR_QUOTE_ITEM_STATUS = {
  ...WORKFLOW_STEP_STATUS,
  NO_QUOTE: 'no_quote',
};

export const WORKFLOW_OR_QUOTE_ITEM_STATUS_LABEL = {
  ...WORKFLOW_STEP_STATUS_LABEL,
  [WORKFLOW_OR_QUOTE_ITEM_STATUS.NO_QUOTE]: 'No Quote',
};

export const WORKFLOW_STEP_STATUS_COLORS = {
  [WORKFLOW_STEP_STATUS.NOT_STARTED]: theme.colors.status.notStarted,
  [WORKFLOW_STEP_STATUS.IN_PROGRESS]: theme.colors.status.inProgress,
  [WORKFLOW_STEP_STATUS.NOT_APPLICABLE]: theme.colors.status.notApplicable,
  [WORKFLOW_STEP_STATUS.COMPLETED]: theme.colors.status.complete,
  [WORKFLOW_STEP_STATUS.ON_HOLD]: theme.colors.status.onHold,
};

export const COST_CATEGORY_UI_VALUES = {
  PURCHASED_COMPONENT: 'purchasedComponent',
  MATERIAL: 'material',
  INSIDE: 'inside',
  OUTSIDE: 'outside',
  CHILD_OVERRIDE: 'childOverride',
  GENERAL: 'general',
  CUSTOM: 'custom',
} as const;

export type CostCategory = ValueOf<typeof COST_CATEGORY_UI_VALUES>;

export const COST_CATEGORY_API_VALUES = {
  PURCHASED_COMPONENT: 'purchased_component',
  MATERIAL: 'material',
  INSIDE: 'inside',
  OUTSIDE: 'outside',
  CHILD_OVERRIDE: 'child_override',
  GENERAL: 'general',
  CUSTOM: 'custom',
} as const;

export const COST_CATEGORY_API_MAP = {
  ...COST_CATEGORY_API_VALUES,
  [COST_CATEGORY_UI_VALUES.PURCHASED_COMPONENT]:
    COST_CATEGORY_API_VALUES.PURCHASED_COMPONENT,
  [COST_CATEGORY_UI_VALUES.MATERIAL]: COST_CATEGORY_API_VALUES.MATERIAL,
  [COST_CATEGORY_UI_VALUES.INSIDE]: COST_CATEGORY_API_VALUES.INSIDE,
  [COST_CATEGORY_UI_VALUES.OUTSIDE]: COST_CATEGORY_API_VALUES.OUTSIDE,
  [COST_CATEGORY_UI_VALUES.GENERAL]: COST_CATEGORY_API_VALUES.GENERAL,
  [COST_CATEGORY_UI_VALUES.CUSTOM]: COST_CATEGORY_API_VALUES.CUSTOM,
  [COST_CATEGORY_API_VALUES.PURCHASED_COMPONENT]:
    COST_CATEGORY_API_VALUES.PURCHASED_COMPONENT,
  [COST_CATEGORY_API_VALUES.MATERIAL]: COST_CATEGORY_API_VALUES.MATERIAL,
  [COST_CATEGORY_API_VALUES.INSIDE]: COST_CATEGORY_API_VALUES.INSIDE,
  [COST_CATEGORY_API_VALUES.OUTSIDE]: COST_CATEGORY_API_VALUES.OUTSIDE,
  [COST_CATEGORY_API_VALUES.GENERAL]: COST_CATEGORY_API_VALUES.GENERAL,
  [COST_CATEGORY_API_VALUES.CUSTOM]: COST_CATEGORY_API_VALUES.CUSTOM,
} as const;

export const COST_CATEGORY = {
  ...COST_CATEGORY_UI_VALUES,
  [COST_CATEGORY_UI_VALUES.PURCHASED_COMPONENT]:
    COST_CATEGORY_UI_VALUES.PURCHASED_COMPONENT,
  [COST_CATEGORY_UI_VALUES.MATERIAL]: COST_CATEGORY_UI_VALUES.MATERIAL,
  [COST_CATEGORY_UI_VALUES.INSIDE]: COST_CATEGORY_UI_VALUES.INSIDE,
  [COST_CATEGORY_UI_VALUES.OUTSIDE]: COST_CATEGORY_UI_VALUES.OUTSIDE,
  [COST_CATEGORY_UI_VALUES.GENERAL]: COST_CATEGORY_UI_VALUES.GENERAL,
  [COST_CATEGORY_UI_VALUES.CUSTOM]: COST_CATEGORY_UI_VALUES.CUSTOM,
  [COST_CATEGORY_API_VALUES.PURCHASED_COMPONENT]:
    COST_CATEGORY_UI_VALUES.PURCHASED_COMPONENT,
  [COST_CATEGORY_API_VALUES.MATERIAL]: COST_CATEGORY_UI_VALUES.MATERIAL,
  [COST_CATEGORY_API_VALUES.INSIDE]: COST_CATEGORY_UI_VALUES.INSIDE,
  [COST_CATEGORY_API_VALUES.OUTSIDE]: COST_CATEGORY_UI_VALUES.OUTSIDE,
  [COST_CATEGORY_API_VALUES.GENERAL]: COST_CATEGORY_UI_VALUES.GENERAL,
  [COST_CATEGORY_API_VALUES.CUSTOM]: COST_CATEGORY_UI_VALUES.CUSTOM,
} as const;

export const COST_CATEGORY_LABEL = {
  PURCHASED_COMPONENT: 'Purchased Component',
  [COST_CATEGORY.PURCHASED_COMPONENT]: 'Purchased Component',
  MATERIAL: 'Material',
  [COST_CATEGORY.MATERIAL]: 'Material',
  INSIDE: 'Inside',
  [COST_CATEGORY.INSIDE]: 'Inside',
  OUTSIDE: 'Outside',
  [COST_CATEGORY.OUTSIDE]: 'Outside',
  GENERAL: 'General',
  [COST_CATEGORY.GENERAL]: 'General',
  CUSTOM: 'New Cost Category',
  [COST_CATEGORY.CUSTOM]: 'New Cost Category',
} as const;

export const COST_CATEGORY_COLOR = {
  PURCHASED_COMPONENT: '#3EDCFF',
  [COST_CATEGORY.PURCHASED_COMPONENT]: '#3EDCFF',
  MATERIAL: '#FF3FB2',
  [COST_CATEGORY.MATERIAL]: '#FF3FB2',
  INSIDE: '#BF6EFF',
  [COST_CATEGORY.INSIDE]: '#BF6EFF',
  OUTSIDE: '#F3AE46',
  [COST_CATEGORY.OUTSIDE]: '#F3AE46',
} as const;

export const CUSTOM_COST_CATEGORY_COLORS = {
  magenta: '#A51A54',
  copper: '#C9500B',
  brass: '#B37702',
  green: '#246B41',
  teal: '#087980',
  blue: '#155CBB',
  purple: '#5E35B5',
  lead: '#4D4D4D',
} as const;

export const PROFIT_ITEM_CALCULATION_TYPE = {
  MARKUP: 'markup',
  MARGIN: 'margin',
};

export const FILTER_EQUAL_OPERATOR = '=';
export const FILTER_DNE_OPERATOR = '!=';

export const CHAMELEON_TOKEN = process.env.CHAMELEON_TOKEN;

export const MANAGED_INTEGRATION_LOGO_PLACEHOLDER = `https://s3.amazonaws.com/static.paperlessparts.com/images/default-integration-logo.png`;

export const PEM_BRAND_LOCATOR = 'pem-fasteners';

export const CHAT_LOCATION = {
  QUOTE: 'quote',
  VIEWER: 'viewer',
};

export const THYSSENKRUPP_LOGO =
  'https://s3.amazonaws.com/static.paperlessparts.com/thyssenkrupp-logo.png';

export const VENDORS_LOGOS = {
  thyssenkrupp: THYSSENKRUPP_LOGO,
  online_metals: ONLINE_METALS_LOGO,
};

export const FINANCE_EMAIL = 'finance@paperlessparts.com';

export const DOWNLOAD_CLOUD_CONNECTOR_URL =
  'https://github.com/part-os/integration-cloud-connector/releases/latest/download/Paperless-Parts-Cloud-Connector-Setup.exe';

export const DOWNLOAD_INTEGRATION_CONNECTOR_URL =
  'https://s3.us-gov-west-1.amazonaws.com/downloads.paperlessparts.com/integration-connector/IntegrationConnectorSetup.exe';

export const INTEGRATION_CONNECTOR_ICON_URL =
  'https://s3.us-east-1.amazonaws.com/static.paperlessparts.com/images/IC-AppIcon_256.png';
